import { Injectable } from 'framework/di';

import { DomHelper } from './dom.helper';

interface FocusedElementRef {
  productId: string;
  isQuantityInput: boolean;
  attributeType: string | null;
}

@Injectable
export class WCAGHelper {
  lastFocusedElementRef: FocusedElementRef | null = null;
  keyboardActivated: boolean = false;
  activationKeys: string[] = ['Enter', ' ', 'Spacebar'];

  constructor(protected domHelper: DomHelper) {
    this.initKeyboardDetection();
  }

  /**
   * Initialize keyboard activation detection
   */
  initKeyboardDetection(): void {
    // Detect when the user interacts using the keyboard globally
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (this.activationKeys.includes(event.key)) {
        this.keyboardActivated = true;
      }
    });

    // Reset the flag after any mouse or touch interaction
    document.addEventListener('mousedown', () => {
      this.keyboardActivated = false;
    });

    document.addEventListener('touchstart', () => {
      this.keyboardActivated = false;
    });
  }

  /**
   * Check if keyboard navigation is currently active
   */
  isKeyboardActivated(): boolean {
    return this.keyboardActivated;
  }

  /**
   * Focus the first focusable element in a modal
   */
  focusFirstElement(
    modal: Element,
    selector: string = 'a[href], button:not([disabled]), input[type=number], [tabindex]:not([tabindex="-1"])'
  ): void {
    const focusableElements = modal.querySelectorAll(selector);
    let firstFocusableElement: Element | null = null;
    const quickViewProductImage = modal.querySelector('.boost-sd__quick-view-product-image');
    const isDisableImage = quickViewProductImage
      ? quickViewProductImage.getAttribute('data-disable-image')
      : null;

    if (isDisableImage === 'true') {
      const productInfoElement = modal.querySelector('.boost-sd__quick-view-product-info');
      if (productInfoElement) {
        firstFocusableElement = productInfoElement;
      }
    } else if (focusableElements.length > 0) {
      firstFocusableElement = focusableElements[0];
    }

    if (firstFocusableElement && firstFocusableElement instanceof HTMLElement) {
      firstFocusableElement.focus();
    }
  }

  /**
   * Trap focus within a modal for keyboard navigation
   */
  trapFocus(
    event: KeyboardEvent,
    modal: Element,
    selector: string = 'a[href], button:not([disabled]), input[type=number], [tabindex]:not([tabindex="-1"])',
    closeButtonSelector: string = '.boost-sd__modal-close-btn'
  ): void {
    const focusableElements = modal.querySelectorAll(selector);
    const firstFocusableElement = this.domHelper.validateHTMLElement(focusableElements[0]);
    const lastFocusableElement = this.domHelper.validateHTMLElement(
      focusableElements[focusableElements.length - 1]
    );

    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement?.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement?.focus();
          event.preventDefault();
        }
      }
    } else if (event.key === 'Escape') {
      const closeButton = this.domHelper.validateHTMLElement(
        modal.querySelector(closeButtonSelector)
      );
      closeButton?.click();
    }
  }

  /**
   * Check if an element is focusable
   */
  isFocusable(element: Element | null): boolean {
    const htmlElement = this.domHelper.validateHTMLElement(element);
    if (!htmlElement) return false;
    const isDisabled = 'disabled' in htmlElement && htmlElement.disabled;

    return !isDisabled && htmlElement.offsetParent !== null;
  }

  /**
   * Save the currently focused element reference for later restoration
   */
  saveFocusElement(element: Element | null): void {
    if (element) {
      const cartItem = element.closest('.boost-sd__cart-item');
      const productId = cartItem?.getAttribute('data-product-id');
      const isQuantityInput = element.tagName.toLowerCase() === 'input';

      if (productId) {
        this.lastFocusedElementRef = {
          productId,
          isQuantityInput,
          attributeType: element.getAttribute('aria-label'),
        };
      }
    }
  }

  /**
   * Restore focus to the previously saved element
   */
  restoreFocusedElement(): void {
    const modal = document.querySelector('#boost-sd__modal-cart');
    if (!modal) return;

    const observer = new MutationObserver((mutations, obs) => {
      const { productId, isQuantityInput, attributeType } = this.lastFocusedElementRef || {};
      let newElement: HTMLElement | null = null;

      if (isQuantityInput) {
        newElement = document.querySelector(
          `[data-product-id="${productId}"] input[type="number"]`
        );
      } else {
        newElement = document.querySelector(
          `[data-product-id="${productId}"] button[aria-label="${attributeType}"]`
        );
      }

      if (newElement && this.isFocusable(newElement)) {
        newElement.focus();
        obs.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
}
